import React, { useEffect, useRef, useState } from 'react';
import { ref, update } from 'firebase/database';
import { toast } from 'react-toastify';
import { FiMapPin, FiCrosshair, FiRadio, FiExternalLink } from 'react-icons/fi';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { db } from '../../firebase';

// Location tab for the device detail page.
//
// The monitoring app publishes the device position under
// mdm/devices/<MAC>/location = { lat, lng, accuracy, time, provider }. It is
// shown on a free OpenStreetMap map (Leaflet, no API key). Two commands drive
// the reporting cadence:
//   location:once  – push a single fresh fix now
//   location:live  – switch to a high-frequency window (~5s for a couple of
//                    minutes) so the map tracks the vehicle live, then the app
//                    reverts to the cheap baseline (on-move / ~60s) on its own.
//
// Until a device runs a monitoring build that reports location, `location` is
// absent and we show a clear "not reporting yet" state.

export default function LocationTab({ mac, device }) {
    const loc = normalizeLocation(device?.location);

    const send = async (command, label) => {
        try {
            await update(ref(db, `mdm/devices/${mac}`), { command });
            toast.success(`${label} requested`);
        } catch (err) {
            toast.error(`${label} failed: ${err.message}`);
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)] gap-6 items-start">
            {/* ---- Map ----------------------------------------------------- */}
            <section className="card-chrome p-5 shadow-sm">
                <header className="mb-4 flex items-center justify-between">
                    <h2 className="inline-flex items-center gap-2 text-base font-semibold text-brand-400">
                        <FiMapPin /> Location
                    </h2>
                    {loc && <FreshnessBadge time={loc.time} />}
                </header>

                {loc ? (
                    <LeafletMap lat={loc.lat} lng={loc.lng} accuracy={loc.accuracy} />
                ) : (
                    <div className="card-inset h-[300px] flex flex-col items-center justify-center text-center px-6">
                        <FiMapPin className="text-3xl text-gray-600 mb-3" />
                        <div className="text-sm font-medium text-gray-300">
                            This device is not reporting location yet
                        </div>
                        <div className="text-xs text-gray-500 mt-1 max-w-sm">
                            It needs a monitoring build with location reporting enabled. Once it
                            reports, the position appears here on the map.
                        </div>
                    </div>
                )}
            </section>

            {/* ---- Details + controls ------------------------------------- */}
            <section className="card-chrome p-5 shadow-sm">
                <header className="mb-4">
                    <h2 className="text-base font-semibold text-brand-400">Details</h2>
                </header>

                <div className="space-y-2">
                    <InfoRow label="Latitude" value={loc ? loc.lat.toFixed(6) : '—'} mono />
                    <InfoRow label="Longitude" value={loc ? loc.lng.toFixed(6) : '—'} mono />
                    <InfoRow
                        label="Accuracy"
                        value={loc && loc.accuracy != null ? `±${Math.round(loc.accuracy)} m` : '—'}
                    />
                    <InfoRow label="Source" value={loc?.provider || '—'} />
                    <InfoRow
                        label="Last fix"
                        value={loc ? formatWhen(loc.time) : '—'}
                    />
                </div>

                {loc && (
                    <a
                        href={`https://www.openstreetmap.org/?mlat=${loc.lat}&mlon=${loc.lng}#map=17/${loc.lat}/${loc.lng}`}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-3 inline-flex items-center gap-1.5 text-xs text-brand-400 hover:text-brand-300"
                    >
                        <FiExternalLink /> Open in OpenStreetMap
                    </a>
                )}

                <Divider className="my-4" />

                <div className="grid grid-cols-2 gap-2">
                    <ActionTile
                        onClick={() => send('location:once', 'Locate now')}
                        icon={<FiCrosshair />}
                        label="Locate"
                        hint="Get a fresh fix now"
                    />
                    <ActionTile
                        onClick={() => send('location:live', 'Live tracking')}
                        icon={<FiRadio />}
                        label="Live"
                        hint="Track for ~2 min"
                    />
                </div>

                <p className="text-xs text-gray-500 mt-3 leading-relaxed">
                    Baseline reporting is cheap: a fix on movement (≈30 m) and at most once a minute.
                    "Live" temporarily raises it to ~5 s, then it reverts automatically.
                </p>
            </section>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Leaflet map (plain Leaflet, no react-leaflet). A single marker + accuracy
// circle; the view recentres whenever the coordinates change.
// ---------------------------------------------------------------------------

function LeafletMap({ lat, lng, accuracy }) {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const markerRef = useRef(null);
    const circleRef = useRef(null);

    // Create the map once, tear it down on unmount (also handles React 18
    // StrictMode's double-invoke in development via the cleanup).
    useEffect(() => {
        if (!containerRef.current || mapRef.current) return undefined;
        const map = L.map(containerRef.current, {
            center: [lat, lng],
            zoom: 16,
            zoomControl: true,
            attributionControl: true,
        });
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '&copy; OpenStreetMap contributors',
        }).addTo(map);
        mapRef.current = map;
        return () => {
            map.remove();
            mapRef.current = null;
            markerRef.current = null;
            circleRef.current = null;
        };
    }, []);

    // Update marker / accuracy circle / view when the fix changes.
    useEffect(() => {
        const map = mapRef.current;
        if (!map || typeof lat !== 'number' || typeof lng !== 'number') return;

        if (!markerRef.current) {
            markerRef.current = L.circleMarker([lat, lng], {
                radius: 8,
                color: '#ffffff',
                weight: 2,
                fillColor: '#2563eb',
                fillOpacity: 1,
            }).addTo(map);
        } else {
            markerRef.current.setLatLng([lat, lng]);
        }

        if (accuracy != null) {
            if (!circleRef.current) {
                circleRef.current = L.circle([lat, lng], {
                    radius: accuracy,
                    color: '#2563eb',
                    weight: 1,
                    fillColor: '#2563eb',
                    fillOpacity: 0.1,
                }).addTo(map);
            } else {
                circleRef.current.setLatLng([lat, lng]);
                circleRef.current.setRadius(accuracy);
            }
        }

        map.setView([lat, lng], map.getZoom());
    }, [lat, lng, accuracy]);

    return (
        <div
            ref={containerRef}
            className="rounded-lg overflow-hidden border border-white/[0.08]"
            style={{ height: 420, width: '100%' }}
        />
    );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function normalizeLocation(value) {
    if (!value) return null;
    const lat = Number(value.lat);
    const lng = Number(value.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return {
        lat,
        lng,
        accuracy: value.accuracy != null ? Number(value.accuracy) : null,
        time: value.time ? Number(value.time) : null,
        provider: value.provider || null,
    };
}

function formatWhen(time) {
    if (!time) return '—';
    const d = new Date(time);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString();
}

function FreshnessBadge({ time }) {
    const [, force] = useState(0);
    // Re-render once a minute so "x min ago" stays current.
    useEffect(() => {
        const id = setInterval(() => force((n) => n + 1), 60000);
        return () => clearInterval(id);
    }, []);
    if (!time) return null;
    const mins = Math.floor((Date.now() - Number(time)) / 60000);
    const label = mins <= 0 ? 'just now' : mins === 1 ? '1 min ago' : `${mins} min ago`;
    const tone =
        mins <= 2
            ? 'bg-green-500/15 text-green-400'
            : mins <= 15
            ? 'bg-yellow-500/15 text-yellow-400'
            : 'bg-white/[0.06] text-gray-400';
    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${tone}`}>
            {label}
        </span>
    );
}

function InfoRow({ label, value, mono = false }) {
    return (
        <div className="flex items-center justify-between gap-3 text-sm">
            <span className="text-gray-500">{label}</span>
            <span className={`text-gray-200 ${mono ? 'font-mono' : ''}`}>{value}</span>
        </div>
    );
}

function ActionTile({ onClick, icon, label, hint }) {
    return (
        <button
            onClick={onClick}
            className="bg-surface-inset border border-white/[0.08] rounded-lg p-4 text-left transition-colors hover:bg-white/[0.06]"
        >
            <div className="inline-flex items-center gap-1.5 text-gray-400 text-xs uppercase tracking-wider">
                {icon} {label}
            </div>
            <div className="mt-1 text-sm font-medium text-white">{hint}</div>
        </button>
    );
}

function Divider({ className = '' }) {
    return <div className={`h-px bg-white/[0.06] ${className}`} />;
}
